import React from 'react';
import { Pressable, StyleSheet, View } from 'react-native';

import { Text } from '~/components/core';
import { useStyles } from '~/hooks';
import { FONT_SIZE_S, FONT_SIZE_XS } from '~/constants/typography';
import { scaleSize } from '~/constants/scale';
import { Theme } from '~/theme';

type Props = {
    // Group heading (i18n key or plain text).
    title: string;
    // Pre-composed summary lines; rendered untranslated.
    lines: string[];
    onEdit?: () => void;
};

// One review group: heading + summary lines + the Edit jump back to its step. Every line is a
// single row — a summary too long for the width truncates with "…" rather than growing the tile,
// so the review reads as a uniform stack of same-height cards. Edit opens the full detail.
export default function SummaryGroup({ title, lines, onEdit }: Props) {
    const styles = useStyles(stylesGenerator);
    return (
        <View style={styles.container}>
            <View style={styles.headerRow}>
                <Text fontType="LATO_BOLD" style={styles.title}>
                    {title}
                </Text>
                {onEdit ? (
                    <Pressable hitSlop={8} onPress={onEdit} style={({ pressed }) => [pressed && styles.pressed]}>
                        <Text fontType="LATO_BOLD" style={styles.editLink}>
                            edit
                        </Text>
                    </Pressable>
                ) : null}
            </View>
            {lines.filter(Boolean).map((line, i) => (
                <Text noTranslate key={i} numberOfLines={1} style={styles.line}>
                    {line}
                </Text>
            ))}
        </View>
    );
}

const stylesGenerator = (theme: Theme) =>
    StyleSheet.create({
        container: {
            backgroundColor: theme.backgroundSecondary,
            borderRadius: scaleSize(8),
            paddingHorizontal: scaleSize(12),
            paddingVertical: scaleSize(10),
            marginBottom: scaleSize(8),
        },
        headerRow: {
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: scaleSize(4),
        },
        title: {
            fontSize: FONT_SIZE_XS,
            color: theme.textSecondary,
            textTransform: 'uppercase',
        },
        editLink: {
            fontSize: FONT_SIZE_XS,
            color: theme.button,
        },
        pressed: {
            opacity: 0.6,
        },
        line: {
            fontSize: FONT_SIZE_S,
            color: theme.text,
            marginTop: scaleSize(2),
        },
    });
